import type { IRecordTypeDefinition } from '@platform/core/types';
import { isKb, visibleFields } from './formRules';
import type { IProtoForm } from './IProtoForm';
import type { IProtoGroup } from './IProtoGroup';
import { standardGroupsOf } from './standardGroups';
import { workspaceFieldsOf } from './workspaceFields';

/** Групи, які запис бази знань показує зі стандартного макета (прототип v4 formGroups, keep для kb): Management · Dates · Planning · Details. */
const KB_KEEP = ['Management', 'Dates', 'Planning', 'Details'];
const sig = (groups: readonly IProtoGroup[]) => JSON.stringify(groups.map((g) => [g.title.en ?? '', g.keys]));

/** Стандартні групи моделі (без змінених редактором груп) за поточним верхом форми. Чиста функція. */
function standardOf(rt: IRecordTypeDefinition, form: IProtoForm): IProtoGroup[] {
  return standardGroupsOf(workspaceFieldsOf(rt), { status: form.key.status, tags: form.showTags, description: false });
}

/**
 * База знань без змінених груп (прототип v4: поки layout.groups немає, запис бере стандартні групи без «Нотаток» і додає групу
 * «Опис» з описом): групи, які зберігає редактор, коли групи моделі — стандартні; інакше — групи моделі (layout.groups прототипу).
 * Не база знань — групи моделі. Чиста функція.
 */
export function savedGroupsOf(rt: IRecordTypeDefinition, form: IProtoForm): IProtoGroup[] {
  if (!isKb(rt) || sig(form.groups) !== sig(standardOf(rt, form))) return form.groups;
  const keep = form.groups.filter((g) => KB_KEEP.includes(g.title.en ?? ''));
  const desc = visibleFields(rt).some((f) => f.key === 'description');
  return desc ? [...keep, { id: 'description', title: { en: 'Description' }, full: false, keys: ['description'] }] : keep;
}

/** Зворотне до savedGroupsOf: збережені групи бази знань, що збігаються з такими стандартними, — це стандартні групи моделі. Чиста функція. */
export function modelGroupsOf(rt: IRecordTypeDefinition, form: IProtoForm): IProtoGroup[] {
  if (!isKb(rt)) return form.groups;
  const standard = standardOf(rt, form);
  return sig(savedGroupsOf(rt, { ...form, groups: standard })) === sig(form.groups) ? standard : form.groups;
}
